import React, { useRef, useState } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { cx } from './hooks';

const optionValue = o => (typeof o === 'object' && o !== null ? o.value : o);
const optionLabel = o => (typeof o === 'object' && o !== null ? o.label : o);

/**
 * Free-text tag list, or a pick-from-options list
 * when `restrictToOptions` is set.
 * Enter or a separator adds the typed text; Backspace on an empty box removes the last tag.
 */
const TagsInput = ({
  value,
  onChange,
  options = [],
  restrictToOptions = false,
  tokenSeparators = [','],
  placeholder,
  disabled = false,
  id,
  className,
}) => {
  const tags = Array.isArray(value) ? value : [];
  const [text, setText] = useState('');
  const inputRef = useRef(null);

  const labelFor = tag => {
    const match = options.find(o => optionValue(o) === tag);
    return match ? optionLabel(match) : tag;
  };

  const addTags = raw => {
    const pieces = raw
      .split(new RegExp(`[${tokenSeparators.map(s => s.replace(/[\\\]^-]/g, '\\$&')).join('')}]`))
      .map(p => p.trim())
      .filter(Boolean);
    if (!pieces.length) return;

    const next = [...tags];
    pieces.forEach(piece => {
      let tag = piece;
      if (restrictToOptions) {
        const match = options.find(
          o => String(optionValue(o)).toLowerCase() === piece.toLowerCase() ||
            String(optionLabel(o)).toLowerCase() === piece.toLowerCase()
        );
        if (!match) return;
        tag = optionValue(match);
      }
      if (!next.includes(tag)) next.push(tag);
    });
    onChange?.(next);
    setText('');
  };

  const remove = tag => onChange?.(tags.filter(t => t !== tag));

  const onKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTags(text);
    } else if (e.key === 'Backspace' && !text && tags.length) {
      remove(tags[tags.length - 1]);
    }
  };

  const onInputChange = e => {
    const typed = e.target.value;
    if (tokenSeparators.some(sep => typed.includes(sep))) addTags(typed);
    else setText(typed);
  };

  const remaining = options.filter(o => {
    const v = optionValue(o);
    if (tags.includes(v)) return false;
    if (!text) return true;
    return String(optionLabel(o)).toLowerCase().includes(text.toLowerCase());
  });

  return (
    <div className={className}>
      <div
        className={cx('tags-input', disabled && 'is-disabled')}
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map(tag => (
          <span key={tag} className="tag">
            {labelFor(tag)}
            {!disabled && (
              <button
                type="button"
                className="tag-close"
                onClick={e => {
                  e.stopPropagation();
                  remove(tag);
                }}
                aria-label={`Remove ${labelFor(tag)}`}
              >
                <AiOutlineClose />
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          value={text}
          onChange={onInputChange}
          onKeyDown={onKeyDown}
          onBlur={() => !restrictToOptions && addTags(text)}
          placeholder={tags.length ? '' : placeholder}
          disabled={disabled}
        />
      </div>
      {remaining.length > 0 && !disabled && (
        <div className="tags-input-options">
          {remaining.slice(0, 30).map(o => (
            <button
              key={String(optionValue(o))}
              type="button"
              className="tags-input-option"
              onClick={() => {
                onChange?.([...tags, optionValue(o)]);
                setText('');
              }}
            >
              + {optionLabel(o)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TagsInput;
